import {useStorage} from '@vueuse/core';

const TIMELINE_PERIOD_TYPES = ['decade', 'year'];
const TIMELINE_SORT_ORDERS = ['newest-to-oldest', 'oldest-to-newest'];
const TIMELINE_PRIVACY_VALUES = ['connections', 'private'];

export const TIMELINE_VISIBILITY_OPTIONS = [
  {
    value: 'connections',
    label: 'Connections',
    description: 'Only people you are connected with can see this',
    icon: 'i-ri-group-line'
  },
  {
    value: 'private',
    label: 'Private',
    description: 'Only you can see this',
    icon: 'i-ri-lock-line'
  }
];

function useTimelineMetadata(userId, {server = false, enabled = true} = {}) {
  const resolvedUserId = computed(() => String(unref(userId)));
  const resolvedEnabled = computed(() => Boolean(unref(enabled)));

  return useAsyncData(
    () => `timeline-metadata-${resolvedUserId.value}`,
    () => resolvedEnabled.value
      ? useTimelineApi().getTimeline({userId: resolvedUserId.value})
      : null,
    {server, watch: [resolvedUserId, resolvedEnabled]}
  );
}

export function useTimeline(userId, {
  server = false,
  enabled = true,
  forcedInitialPeriodType = null
} = {}) {
  const resolvedUserId = computed(() => String(unref(userId)));
  const resolvedEnabled = computed(() => Boolean(unref(enabled)));
  const preferencesReady = ref(false);
  const periodType = ref('decade');
  const savedPeriodType = useStorage(
    'forever.timeline.period-type',
    '',
    undefined,
    {initOnMounted: true}
  );
  const sortOrder = useStorage(
    'forever.timeline.sort-order',
    'newest-to-oldest',
    undefined,
    {initOnMounted: true}
  );

  watch(periodType, value => {
    if (!TIMELINE_PERIOD_TYPES.includes(value)) periodType.value = 'decade';
  }, {immediate: true});

  watch(sortOrder, value => {
    if (!TIMELINE_SORT_ORDERS.includes(value)) sortOrder.value = 'newest-to-oldest';
  }, {immediate: true});

  function setPeriodType(value) {
    if (!TIMELINE_PERIOD_TYPES.includes(value)) return;
    periodType.value = value;
    savedPeriodType.value = value;
  }

  onMounted(() => nextTick(() => {
    preferencesReady.value = true;
  }));

  const {
    data: timeline,
    pending: timelinePending,
    error: timelineError,
    refresh: refreshTimelineMetadata
  } = useTimelineMetadata(resolvedUserId, {server, enabled: resolvedEnabled});

  const periodCollections = reactive({decade: null, year: null});
  const periodPending = reactive({decade: false, year: false});
  const periodErrors = shallowReactive({decade: null, year: null});
  const periodRequests = new Map();
  const decadePeriods = reactive(new Map());
  const initializationPending = ref(false);
  const initializedTimelineId = ref(null);
  let periodGeneration = 0;

  function clearPeriodCollections() {
    periodGeneration += 1;
    TIMELINE_PERIOD_TYPES.forEach(type => {
      periodCollections[type] = null;
      periodPending[type] = false;
      periodErrors[type] = null;
    });
    periodRequests.clear();
    decadePeriods.clear();
    initializedTimelineId.value = null;
    initializationPending.value = false;
  }

  async function loadPeriods(type, {refresh = false} = {}) {
    if (!TIMELINE_PERIOD_TYPES.includes(type) || !timeline.value?.id || !resolvedEnabled.value) {
      return [];
    }

    if (!refresh && periodCollections[type]) return periodCollections[type];
    if (periodRequests.has(type)) return periodRequests.get(type);

    periodPending[type] = true;
    periodErrors[type] = null;
    const requestGeneration = periodGeneration;

    const request = useTimelineApi().getPeriods({
      userId: resolvedUserId.value,
      timelineId: timeline.value.id,
      type
    }).then(periods => {
      if (requestGeneration === periodGeneration) {
        periodCollections[type] = periods;
      }
      return periods;
    }).catch(error => {
      if (requestGeneration === periodGeneration) {
        periodErrors[type] = error;
      }
      throw error;
    }).finally(() => {
      if (periodRequests.get(type) === request) {
        periodPending[type] = false;
        periodRequests.delete(type);
      }
    });

    periodRequests.set(type, request);
    return request;
  }

  async function initializePeriodView() {
    const timelineId = timeline.value?.id;
    if (
      !preferencesReady.value
      || !timelineId
      || !resolvedEnabled.value
      || initializedTimelineId.value === timelineId
      || initializationPending.value
    ) {
      return;
    }

    initializationPending.value = true;
    const requestGeneration = periodGeneration;

    try {
      const decades = await loadPeriods('decade');
      if (requestGeneration !== periodGeneration || timeline.value?.id !== timelineId) return;

      const forcedType = unref(forcedInitialPeriodType);
      periodType.value = TIMELINE_PERIOD_TYPES.includes(forcedType)
        ? forcedType
        : TIMELINE_PERIOD_TYPES.includes(savedPeriodType.value)
          ? savedPeriodType.value
          : decades.length >= 3 ? 'decade' : 'year';

      if (decades.length && periodType.value === 'year') {
        await loadPeriods('year');
      }

      if (requestGeneration === periodGeneration && timeline.value?.id === timelineId) {
        initializedTimelineId.value = timelineId;
      }
    } finally {
      if (requestGeneration === periodGeneration) {
        initializationPending.value = false;
      }
    }
  }

  let loadedTimelineId = null;
  watch(
    [preferencesReady, () => timeline.value?.id, resolvedEnabled],
    ([ready, timelineId, isEnabled]) => {
      if (timelineId !== loadedTimelineId) {
        clearPeriodCollections();
        loadedTimelineId = timelineId || null;
      }

      if (ready && timelineId && isEnabled) {
        initializePeriodView().catch(() => null);
      }
    },
    {immediate: true}
  );

  watch(periodType, activeType => {
    if (
      initializedTimelineId.value === timeline.value?.id
      && resolvedEnabled.value
      && TIMELINE_PERIOD_TYPES.includes(activeType)
    ) {
      loadPeriods(activeType).catch(() => null);
    }
  });

  function sortPeriods(periods) {
    return [...periods]
      .sort((a, b) => (
        sortOrder.value === 'newest-to-oldest'
          ? b.value - a.value
          : a.value - b.value
      ))
      .map((period, index) => ({
        ...period,
        displayPosition: index % 2 === 0 ? 'top' : 'bottom'
      }));
  }

  const displayedDecades = computed(() => sortPeriods(periodCollections.decade || []));
  const displayedYears = computed(() => sortPeriods(periodCollections.year || []));
  const activePeriodsPending = computed(() => periodPending[periodType.value]);
  const activePeriodsError = computed(() => periodErrors[periodType.value]);
  const activePeriodsMissing = computed(() => periodCollections[periodType.value] === null);
  const empty = computed(() => (
    initializedTimelineId.value === timeline.value?.id
    && periodCollections.decade?.length === 0
  ));
  const pending = computed(() => (
    timelinePending.value
    || (resolvedEnabled.value && !preferencesReady.value)
    || initializationPending.value
    || (
      resolvedEnabled.value
      && preferencesReady.value
      && !timelineError.value
      && !periodErrors.decade
      && !activePeriodsError.value
      && Boolean(timeline.value?.id)
      && (
        initializedTimelineId.value !== timeline.value.id
        || (!empty.value && (activePeriodsPending.value || activePeriodsMissing.value))
      )
    )
  ));
  const error = computed(() => timelineError.value || periodErrors.decade || activePeriodsError.value);

  const visibility = computed(() => timeline.value?.privacy || 'connections');
  const visibilityReady = computed(() => (
    resolvedEnabled.value && !timelinePending.value && !timelineError.value && Boolean(timeline.value?.id)
  ));
  const visibilityLabel = computed(() => (
    TIMELINE_VISIBILITY_OPTIONS.find(option => option.value === visibility.value)?.label
    || 'Connections'
  ));

  async function updateVisibility(value) {
    if (!TIMELINE_PRIVACY_VALUES.includes(value) || !timeline.value?.id) return;

    timeline.value = await useTimelineApi().updateTimeline({
      userId: resolvedUserId.value,
      timelineId: timeline.value.id,
      updates: {privacy: value}
    });
  }

  async function getYearsForDecade(decade, {refresh = false} = {}) {
    if (!refresh && decadePeriods.has(decade.id)) return decadePeriods.get(decade.id);

    const period = await useTimelineApi().getPeriod({
      userId: resolvedUserId.value,
      timelineId: timeline.value.id,
      periodId: decade.id
    });
    const years = period.periods || [];
    decadePeriods.set(decade.id, years);
    return years;
  }

  function applyPeriodUpdate(updatedPeriod) {
    if (!updatedPeriod?.id) return;

    const updateCollection = periods => {
      const existingPeriod = periods?.find(period => period.id === updatedPeriod.id);
      if (existingPeriod) Object.assign(existingPeriod, updatedPeriod);
    };

    updateCollection(periodCollections[updatedPeriod.type]);
    decadePeriods.forEach(updateCollection);

    if (updatedPeriod.type === 'decade' && Array.isArray(updatedPeriod.periods)) {
      decadePeriods.set(updatedPeriod.id, updatedPeriod.periods);
    }
  }

  async function refresh() {
    clearPeriodCollections();
    await refreshTimelineMetadata();

    if (
      preferencesReady.value
      && timeline.value?.id
      && resolvedEnabled.value
      && !timelineError.value
    ) {
      await initializePeriodView();
    }
  }

  async function refreshPeriodCollections() {
    if (!timeline.value?.id || !resolvedEnabled.value) return;

    const shouldRefreshYears = periodType.value === 'year' || periodCollections.year !== null;
    const requests = [loadPeriods('decade', {refresh: true})];
    if (shouldRefreshYears) {
      requests.push(loadPeriods('year', {refresh: true}));
    }

    await Promise.all(requests);
  }

  return {
    timeline,
    pending,
    error,
    empty,
    refresh,
    refreshPeriodCollections,
    preferencesReady,
    periodType,
    setPeriodType,
    sortOrder,
    displayedDecades,
    displayedYears,
    loadPeriods,
    getYearsForDecade,
    applyPeriodUpdate,
    visibility,
    visibilityOptions: TIMELINE_VISIBILITY_OPTIONS,
    visibilityLabel,
    visibilityReady,
    updateVisibility
  };
}

export function useTimelinePeriod(userId, periodId, {server = true} = {}) {
  const resolvedUserId = computed(() => String(unref(userId)));
  const resolvedPeriodId = computed(() => String(unref(periodId)));

  return useAsyncData(
    () => `timeline-period-${resolvedUserId.value}-${resolvedPeriodId.value}`,
    async nuxtApp => {
      const timeline = await nuxtApp.runWithContext(() => useTimelineApi().getTimeline({
        userId: resolvedUserId.value
      }));
      // The second request starts after an await, when SSR no longer has implicit Nuxt context.
      const period = await nuxtApp.runWithContext(() => useTimelineApi().getPeriod({
        userId: resolvedUserId.value,
        timelineId: timeline.id,
        periodId: resolvedPeriodId.value
      }));
      return {timeline, period};
    },
    {server, watch: [resolvedUserId, resolvedPeriodId]}
  );
}

export function formatTimelinePeriodLabel(period) {
  if (!period) return '';
  return period.type === 'decade' ? `${period.value}s` : String(period.value);
}

export function formatTimelineMemoryCount(count) {
  return `${count} ${count === 1 ? 'Memory' : 'Memories'}`;
}
